import * as React from 'react'
import { Link } from 'react-router-dom-v5-compat'

import { clsx } from 'clsx'

import {
  Button,
  Card,
  IdeasIcon,
  Text,
  Skeleton,
  ScrollArea,
  IconButton,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@buffer-mono/popcorn'

import { useTemplates } from '../../hooks/useTemplates'
import { TemplateMiniCardButton } from '../Template/TemplateMiniCard'

import styles from './TemplateSuggestionsCard.module.css'

const NUM_TEMPLATES_TO_SHOW = 8

export const TemplateSuggestionsCard = ({
  toggle,
  cta,
  preselectChannelId,
}: {
  toggle?: boolean
  cta: CTA
  preselectChannelId?: string
}): JSX.Element => {
  const { templates, loading } = useTemplates()
  const [open, setOpen] = React.useState(!toggle)

  const randomTemplates = React.useMemo(() => {
    if (templates.length <= NUM_TEMPLATES_TO_SHOW) return templates
    const shuffled = [...templates].sort(() => 0.5 - Math.random())
    return shuffled.slice(0, NUM_TEMPLATES_TO_SHOW)
  }, [templates])

  return (
    <Card
      className={clsx(styles.container, open ? styles.open : styles.closed)}
      padding="small"
      onClick={(e): void => {
        if (!open) {
          e.preventDefault()
          setOpen(true)
        }
      }}
    >
      <div className={styles.header}>
        <Text weight="medium">
          <IdeasIcon
            size={16}
            style={{ verticalAlign: 'middle', transform: 'translateY(-1px)' }}
          />{' '}
          Not sure what to post? We&apos;ve got a growing list of templates to
          help you
        </Text>
        <div className={styles.actions}>
          {open && (
            <Button as={Link} to="/create/templates" variant="tertiary">
              View All Templates
            </Button>
          )}
          {toggle && (
            <IconButton
              label={open ? 'Hide templates' : 'Show templates'}
              onClick={(): void => setOpen(!open)}
              variant="tertiary"
            >
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          )}
        </div>
      </div>
      {open && (
        <ScrollArea
          scrollbars="horizontal"
          visibility="hover"
          gradientFade={40}
        >
          <div className={styles.templatesGrid}>
            {/* render Skeletons while loading */}
            {loading
              ? Array.from({ length: NUM_TEMPLATES_TO_SHOW }).map((_, idx) => (
                  <Skeleton key={idx} height={140} />
                ))
              : randomTemplates.map((template) => (
                  <TemplateMiniCardButton
                    key={template.id}
                    template={template}
                    cta={cta}
                    preselectChannelId={preselectChannelId}
                    variant="small"
                  />
                ))}
          </div>
        </ScrollArea>
      )}
    </Card>
  )
}
